import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table'
import { common, createLowlight } from 'lowlight'
import { useCallback, useEffect, useRef } from 'react'
import TurndownService from 'turndown'
import { marked } from 'marked'
import BubbleMenuBar from './BubbleMenuBar'
import { SlashCommands } from './SlashCommandMenu'
import { CommentMark } from './extensions/CommentMark'
import type { Comment } from '../../hooks/useComments'
import type { Editor } from '@tiptap/core'

const lowlight = createLowlight(common)

const turndownService = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  br: '  \n',
})

// Task list support for turndown
turndownService.addRule('taskList', {
  filter: (node) => node.nodeName === 'LI' && node.querySelector('input[type="checkbox"]') !== null,
  replacement: (_content, node) => {
    const checkbox = (node as HTMLElement).querySelector('input[type="checkbox"]')
    const checked = checkbox?.hasAttribute('checked') ? 'x' : ' '
    const text = (node as HTMLElement).textContent?.replace(/^\s+/, '') || ''
    return `- [${checked}] ${text}\n`
  },
})

// Table support for turndown
turndownService.addRule('tableCell', {
  filter: ['th', 'td'],
  replacement: (content) => ` ${content.trim()} |`,
})

turndownService.addRule('tableRow', {
  filter: 'tr',
  replacement: (content) => `|${content}\n`,
})

turndownService.addRule('table', {
  filter: 'table',
  replacement: (_content, node) => {
    const rows = Array.from((node as HTMLElement).querySelectorAll('tr'))
    if (rows.length === 0) return ''

    const lines: string[] = []
    rows.forEach((row, i) => {
      const cells = Array.from(row.querySelectorAll('th, td'))
      const line = '| ' + cells.map(c => (c.textContent || '').trim()).join(' | ') + ' |'
      lines.push(line)
      if (i === 0) {
        lines.push('| ' + cells.map(() => '---').join(' | ') + ' |')
      }
    })
    return '\n' + lines.join('\n') + '\n'
  },
})

// Strip comment highlights from markdown output (marks are ephemeral)
turndownService.addRule('commentHighlight', {
  filter: (node) => node.nodeName === 'SPAN' && (node as HTMLElement).hasAttribute('data-comment-id'),
  replacement: (content) => content,
})

// Configure marked to treat single newlines as <br>
marked.setOptions({
  breaks: true,
})

export function htmlToMarkdown(html: string): string {
  return turndownService.turndown(html)
}

export function markdownToHtml(markdown: string): string {
  let html = marked.parse(markdown) as string
  // TipTap's Table extension doesn't parse <thead>/<tbody> tags, so we strip them
  // while keeping the content. This allows TipTap to properly parse table rows.
  html = html.replace(/<\/?thead>/g, '').replace(/<\/?tbody>/g, '')
  return html
}

function applyCommentMarks(editor: Editor, comments: Comment[]) {
  const unresolvedRoots = comments.filter(c => !c.resolved)
  if (unresolvedRoots.length === 0) return

  const { tr } = editor.state
  let modified = false

  for (const comment of unresolvedRoots) {
    const anchor = comment.anchor_text
    if (!anchor) continue

    let found = false
    editor.state.doc.descendants((node, pos) => {
      if (found || !node.isText || !node.text) return
      const idx = node.text.indexOf(anchor)
      if (idx === -1) return

      const from = pos + idx
      const to = from + anchor.length
      tr.addMark(from, to, editor.schema.marks.comment.create({ commentId: comment.id }))
      modified = true
      found = true
    })
  }

  if (modified) {
    tr.setMeta('addToHistory', false)
    editor.view.dispatch(tr)
  }
}

interface Props {
  content: string
  onChange: (markdown: string) => void
  onSave?: () => void
  borderless?: boolean
  showBubbleMenu?: boolean
  showSlashCommands?: boolean
  placeholder?: string
  noteSlug?: string
  comments?: Comment[]
  onAddComment?: (anchorText: string, body: string) => Promise<Comment | null>
  onCommentClick?: (commentId: string) => void
}

export type { Comment }

export default function TipTapEditor({
  content,
  onChange,
  onSave,
  borderless = false,
  showBubbleMenu = false,
  showSlashCommands = false,
  placeholder = 'Start writing...',
  noteSlug,
  comments: externalComments,
  onAddComment,
  onCommentClick,
}: Props) {
  const extensions = [
    StarterKit.configure({
      codeBlock: false,
    }),
    TaskList,
    TaskItem.configure({ nested: true }),
    Link.configure({ openOnClick: true }),
    Placeholder.configure({ placeholder }),
    CodeBlockLowlight.configure({ lowlight }),
    Table.configure({ resizable: false }),
    TableRow,
    TableHeader,
    TableCell,
    ...(showSlashCommands ? [SlashCommands] : []),
    CommentMark,
  ]

  // Skip the first onUpdate fired by TipTap when setting initial content
  const initializedRef = useRef(false)

  const editor = useEditor({
    extensions,
    content: content ? markdownToHtml(content) : '',
    immediatelyRender: false,
    onCreate: () => {
      // Mark as initialized after first render cycle
      requestAnimationFrame(() => { initializedRef.current = true })
    },
    onUpdate: ({ editor }) => {
      if (!initializedRef.current) return
      const html = editor.getHTML()
      const md = htmlToMarkdown(html)
      onChange(md)
    },
    editorProps: {
      attributes: {
        class: borderless
          ? 'prose prose-gray max-w-none focus:outline-none min-h-[60vh] pb-32 notion-editor'
          : 'prose prose-gray max-w-none focus:outline-none min-h-[300px] px-4 py-3',
      },
    },
  })

  // Apply comment marks after editor init + comments loaded
  const marksAppliedRef = useRef(false)
  useEffect(() => {
    if (editor && externalComments && externalComments.length > 0 && !marksAppliedRef.current && initializedRef.current) {
      applyCommentMarks(editor, externalComments)
      marksAppliedRef.current = true
    }
  }, [editor, externalComments])

  // Handle clicks on comment highlights
  useEffect(() => {
    if (!editor || !onCommentClick) return
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const commentSpan = target.closest('[data-comment-id]') as HTMLElement | null
      if (commentSpan) {
        const commentId = commentSpan.getAttribute('data-comment-id')
        if (commentId) onCommentClick(commentId)
      }
    }
    editor.view.dom.addEventListener('click', handleClick)
    return () => editor.view.dom.removeEventListener('click', handleClick)
  }, [editor, onCommentClick])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault()
      onSave?.()
    }
  }, [onSave])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  if (!editor) return null

  return (
    <div className={borderless ? '' : 'rounded-md border border-gray-200 bg-white'}>
      {showBubbleMenu && (
        <BubbleMenuBar
          editor={editor}
          noteSlug={noteSlug}
          onAddComment={onAddComment}
        />
      )}
      <EditorContent editor={editor} />
    </div>
  )
}

export type { Props as TipTapEditorProps }
